/**
 * Google Setup Wizard
 *
 * Step-by-step guided setup for Google OAuth credentials.
 * Shows visual instructions and allows pasting credentials directly.
 */

import React, { useState } from 'react';
import {
  X,
  ExternalLink,
  ChevronRight,
  ChevronLeft,
  Check,
  Copy,
  AlertCircle,
  Loader,
  CheckCircle,
} from 'lucide-react';

const styles = {
  overlay: {
    position: 'fixed' as const,
    inset: 0,
    background: 'rgba(0,0,0,0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
  },
  modal: {
    background: '#fff',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '600px',
    maxHeight: '90vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column' as const,
    boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 24px',
    borderBottom: '1px solid #e5e5e5',
    background: '#fafafa',
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontSize: '18px',
    fontWeight: 600,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    padding: '8px',
    cursor: 'pointer',
    borderRadius: '8px',
    color: '#666',
  },
  progress: {
    display: 'flex',
    gap: '8px',
    padding: '16px 24px',
    background: '#f5f5f5',
    borderBottom: '1px solid #e5e5e5',
  },
  progressStep: {
    flex: 1,
    height: '4px',
    borderRadius: '2px',
    background: '#ddd',
    transition: 'background 0.3s',
  },
  progressStepActive: {
    background: '#4285f4',
  },
  progressStepDone: {
    background: '#34a853',
  },
  body: {
    flex: 1,
    overflow: 'auto',
    padding: '24px',
  },
  stepTitle: {
    fontSize: '20px',
    fontWeight: 600,
    marginBottom: '8px',
    color: '#202124',
  },
  stepDescription: {
    fontSize: '14px',
    color: '#5f6368',
    marginBottom: '24px',
    lineHeight: 1.6,
  },
  instruction: {
    display: 'flex',
    gap: '16px',
    padding: '16px',
    background: '#f8f9fa',
    borderRadius: '12px',
    marginBottom: '16px',
    alignItems: 'flex-start',
  },
  instructionNumber: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    background: '#4285f4',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: 600,
    flexShrink: 0,
  },
  instructionText: {
    flex: 1,
    fontSize: '14px',
    lineHeight: 1.6,
  },
  highlight: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '2px 8px',
    background: '#e8f0fe',
    borderRadius: '4px',
    color: '#1967d2',
    fontWeight: 500,
    fontSize: '13px',
  },
  codeBlock: {
    display: 'block',
    padding: '12px 16px',
    background: '#1e1e1e',
    color: '#d4d4d4',
    borderRadius: '8px',
    fontFamily: 'monospace',
    fontSize: '13px',
    marginTop: '12px',
    position: 'relative' as const,
  },
  copyBtn: {
    position: 'absolute' as const,
    top: '8px',
    right: '8px',
    background: '#333',
    border: 'none',
    padding: '6px',
    borderRadius: '4px',
    cursor: 'pointer',
    color: '#fff',
  },
  openBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 20px',
    background: '#4285f4',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    marginTop: '16px',
  },
  inputGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: 500,
    marginBottom: '8px',
    color: '#202124',
  },
  input: {
    width: '100%',
    padding: '12px 16px',
    border: '2px solid #e5e5e5',
    borderRadius: '8px',
    fontSize: '14px',
    fontFamily: 'monospace',
    transition: 'border-color 0.2s',
    outline: 'none',
    boxSizing: 'border-box' as const,
  },
  inputFocused: {
    borderColor: '#4285f4',
  },
  inputError: {
    borderColor: '#ea4335',
  },
  inputValid: {
    borderColor: '#34a853',
  },
  hint: {
    fontSize: '12px',
    color: '#5f6368',
    marginTop: '6px',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '16px 24px',
    borderTop: '1px solid #e5e5e5',
    background: '#fafafa',
  },
  btn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    border: 'none',
    transition: 'background 0.2s',
  },
  btnPrimary: {
    background: '#4285f4',
    color: '#fff',
  },
  btnSecondary: {
    background: '#f1f3f4',
    color: '#202124',
  },
  btnDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  successBox: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    padding: '40px 20px',
    textAlign: 'center' as const,
  },
  successIcon: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    background: '#e6f4ea',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '24px',
  },
  errorBox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '16px',
    background: '#fce8e6',
    borderRadius: '8px',
    marginBottom: '20px',
    color: '#c5221f',
    fontSize: '14px',
  },
};

interface GoogleSetupWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}

const TOTAL_STEPS = 4;
const GOOGLE_CONSOLE_URL = 'https://console.cloud.google.com/apis/credentials';
const REDIRECT_URI = 'http://localhost:5173/oauth/google/callback';

export function GoogleSetupWizard({ isOpen, onClose, onComplete }: GoogleSetupWizardProps) {
  const [step, setStep] = useState(1);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleCopyRedirectUri = () => {
    navigator.clipboard.writeText(REDIRECT_URI);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleOpenConsole = () => {
    window.open(GOOGLE_CONSOLE_URL, '_blank');
  };

  const validateCredentials = () => {
    if (!clientId.trim()) return 'Client ID is required';
    if (!clientId.includes('.apps.googleusercontent.com')) {
      return 'Invalid Client ID format';
    }
    if (!clientSecret.trim()) return 'Client Secret is required';
    if (clientSecret.length < 10) return 'Client Secret seems too short';
    return null;
  };

  const handleSave = async () => {
    const validationError = validateCredentials();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const response = await fetch('/api/google/auth/setup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientId: clientId.trim(),
          clientSecret: clientSecret.trim(),
        }),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to save credentials');
      }

      setStep(TOTAL_STEPS);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save credentials');
    } finally {
      setIsSaving(false);
    }
  };

  const handleComplete = () => {
    onComplete();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerTitle}>
            <img
              src="https://www.gstatic.com/images/branding/product/1x/googleg_48dp.png"
              alt="Google"
              width="24"
              height="24"
            />
            Setup Google Workspace
          </div>
          <button style={styles.closeBtn} onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Progress */}
        <div style={styles.progress}>
          {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
            <div
              key={i}
              style={{
                ...styles.progressStep,
                ...(i + 1 < step ? styles.progressStepDone : {}),
                ...(i + 1 === step ? styles.progressStepActive : {}),
              }}
            />
          ))}
        </div>

        {/* Body */}
        <div style={styles.body}>
          {/* Step 1: Open Google Console */}
          {step === 1 && (
            <>
              <h2 style={styles.stepTitle}>Open Google Cloud Console</h2>
              <p style={styles.stepDescription}>
                First, we need to create OAuth credentials in your Google Cloud account.
                This is a one-time setup that takes about 2 minutes.
              </p>

              <div style={styles.instruction}>
                <div style={styles.instructionNumber}>1</div>
                <div style={styles.instructionText}>
                  Click the button below to open <span style={styles.highlight}>Google Cloud Console</span>
                </div>
              </div>

              <div style={styles.instruction}>
                <div style={styles.instructionNumber}>2</div>
                <div style={styles.instructionText}>
                  Sign in with your Google account (or create one if needed)
                </div>
              </div>

              <div style={styles.instruction}>
                <div style={styles.instructionNumber}>3</div>
                <div style={styles.instructionText}>
                  If prompted, create a new project or select an existing one
                </div>
              </div>

              <button style={styles.openBtn} onClick={handleOpenConsole}>
                <ExternalLink size={18} />
                Open Google Cloud Console
              </button>
            </>
          )}

          {/* Step 2: Create Credentials */}
          {step === 2 && (
            <>
              <h2 style={styles.stepTitle}>Create OAuth Credentials</h2>
              <p style={styles.stepDescription}>
                Now let's create the OAuth credentials that allow this app to connect to your Google account.
              </p>

              <div style={styles.instruction}>
                <div style={styles.instructionNumber}>1</div>
                <div style={styles.instructionText}>
                  Click <span style={styles.highlight}>+ CREATE CREDENTIALS</span> at the top
                </div>
              </div>

              <div style={styles.instruction}>
                <div style={styles.instructionNumber}>2</div>
                <div style={styles.instructionText}>
                  Select <span style={styles.highlight}>OAuth client ID</span>
                </div>
              </div>

              <div style={styles.instruction}>
                <div style={styles.instructionNumber}>3</div>
                <div style={styles.instructionText}>
                  Choose <span style={styles.highlight}>Web application</span> as the application type
                </div>
              </div>

              <div style={styles.instruction}>
                <div style={styles.instructionNumber}>4</div>
                <div style={styles.instructionText}>
                  Under <strong>"Authorized redirect URIs"</strong>, click <span style={styles.highlight}>+ ADD URI</span> and paste this URL:
                  <div style={styles.codeBlock}>
                    {REDIRECT_URI}
                    <button
                      style={styles.copyBtn}
                      onClick={handleCopyRedirectUri}
                      title="Copy to clipboard"
                    >
                      {copied ? <Check size={14} /> : <Copy size={14} />}
                    </button>
                  </div>
                </div>
              </div>

              <div style={styles.instruction}>
                <div style={styles.instructionNumber}>5</div>
                <div style={styles.instructionText}>
                  Click <span style={styles.highlight}>CREATE</span>
                </div>
              </div>
            </>
          )}

          {/* Step 3: Copy Credentials */}
          {step === 3 && (
            <>
              <h2 style={styles.stepTitle}>Paste Your Credentials</h2>
              <p style={styles.stepDescription}>
                Copy the Client ID and Client Secret from the popup and paste them below.
              </p>

              {error && (
                <div style={styles.errorBox}>
                  <AlertCircle size={18} />
                  <span>{error}</span>
                </div>
              )}

              <div style={styles.inputGroup}>
                <label style={styles.label}>Client ID</label>
                <input
                  type="text"
                  style={{
                    ...styles.input,
                    ...(clientId && clientId.includes('.apps.googleusercontent.com')
                      ? styles.inputValid
                      : {}),
                  }}
                  placeholder="xxxxxxxxxxxx.apps.googleusercontent.com"
                  value={clientId}
                  onChange={(e) => {
                    setClientId(e.target.value);
                    setError(null);
                  }}
                />
                <p style={styles.hint}>Ends with .apps.googleusercontent.com</p>
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Client Secret</label>
                <input
                  type="password"
                  style={{
                    ...styles.input,
                    ...(clientSecret && clientSecret.length > 10 ? styles.inputValid : {}),
                  }}
                  placeholder="GOCSPX-xxxxxxxxxxxxxxxxxxxxxxxxx"
                  value={clientSecret}
                  onChange={(e) => {
                    setClientSecret(e.target.value);
                    setError(null);
                  }}
                />
                <p style={styles.hint}>Starts with GOCSPX-</p>
              </div>
            </>
          )}

          {/* Step 4: Success */}
          {step === TOTAL_STEPS && (
            <div style={styles.successBox}>
              <div style={styles.successIcon}>
                <CheckCircle size={40} color="#34a853" />
              </div>
              <h2 style={styles.stepTitle}>Setup Complete!</h2>
              <p style={styles.stepDescription}>
                Your Google credentials have been saved successfully.
                You can now connect your Google account to access Gmail, Calendar, Drive, and more.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          {step < TOTAL_STEPS ? (
            <>
              <button
                style={{
                  ...styles.btn,
                  ...styles.btnSecondary,
                  ...(step === 1 ? { visibility: 'hidden' as const } : {}),
                }}
                onClick={() => setStep(step - 1)}
                disabled={step === 1}
              >
                <ChevronLeft size={18} />
                Back
              </button>

              {step === 3 ? (
                <button
                  style={{
                    ...styles.btn,
                    ...styles.btnPrimary,
                    ...(isSaving || !clientId || !clientSecret ? styles.btnDisabled : {}),
                  }}
                  onClick={handleSave}
                  disabled={isSaving || !clientId || !clientSecret}
                >
                  {isSaving ? (
                    <>
                      <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
                      Saving...
                    </>
                  ) : (
                    <>
                      Save & Continue
                      <ChevronRight size={18} />
                    </>
                  )}
                </button>
              ) : (
                <button
                  style={{ ...styles.btn, ...styles.btnPrimary }}
                  onClick={() => setStep(step + 1)}
                >
                  Next
                  <ChevronRight size={18} />
                </button>
              )}
            </>
          ) : (
            <>
              <div />
              <button
                style={{ ...styles.btn, ...styles.btnPrimary }}
                onClick={handleComplete}
              >
                Connect Google Account
                <ChevronRight size={18} />
              </button>
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
